import { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  TextArea,
  Select,
  Toggle,
  Button,
  Alert,
  Spinner,
} from '@kollektive/design-system';
import { Zap, Trash2 } from 'lucide-react';
import type {
  Connection,
  DatabaseDialect,
  CreateConnectionRequest,
  UpdateConnectionRequest,
  TestConnectionRequest,
  TestConnectionResponse,
} from '@/types';
import styles from './ConnectionFormModal.module.css';

interface ConnectionFormModalProps {
  open: boolean;
  onClose: () => void;
  connection?: Connection | null; // null/undefined = add mode, Connection = edit mode
  onSave: (data: CreateConnectionRequest | UpdateConnectionRequest) => Promise<void>;
  onDelete?: (id: string) => Promise<void>;
  onTest: (data: TestConnectionRequest) => Promise<TestConnectionResponse>;
  preselectedDialect?: DatabaseDialect;
}

const DIALECT_OPTIONS = [
  { value: 'postgres', label: 'PostgreSQL' },
  { value: 'mysql', label: 'MySQL' },
  { value: 'redshift', label: 'Amazon Redshift' },
];

const SSL_MODE_OPTIONS = [
  { value: '', label: 'Default' },
  { value: 'disable', label: 'Disable' },
  { value: 'require', label: 'Require' },
  { value: 'verify-ca', label: 'Verify CA' },
  { value: 'verify-full', label: 'Verify Full' },
];

const DEFAULT_PORTS: Record<DatabaseDialect, number> = {
  postgres: 5432,
  mysql: 3306,
  redshift: 5439,
};

interface FormData {
  name: string;
  description: string;
  dialect: DatabaseDialect;
  host: string;
  port: string;
  database_name: string;
  username: string;
  password: string;
  ssl_mode: string;
  read_only: boolean;
  max_rows: string;
  query_timeout_seconds: string;
}

interface FormErrors {
  name?: string;
  host?: string;
  port?: string;
  database_name?: string;
  username?: string;
  password?: string;
}

const initialFormData: FormData = {
  name: '',
  description: '',
  dialect: 'postgres',
  host: '',
  port: '5432',
  database_name: '',
  username: '',
  password: '',
  ssl_mode: '',
  read_only: false,
  max_rows: '',
  query_timeout_seconds: '',
};

export function ConnectionFormModal({
  open,
  onClose,
  connection,
  onSave,
  onDelete,
  onTest,
  preselectedDialect,
}: ConnectionFormModalProps) {
  const isEditMode = !!connection;
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<TestConnectionResponse | null>(null);

  // Reset form when modal opens/closes or connection changes
  useEffect(() => {
    if (open) {
      if (connection) {
        setFormData({
          name: connection.name,
          description: connection.description || '',
          dialect: connection.dialect,
          host: connection.host,
          port: String(connection.port),
          database_name: connection.database_name,
          username: connection.username,
          password: '', // Password is never returned from API
          ssl_mode: connection.ssl_mode || '',
          read_only: connection.read_only,
          max_rows: connection.max_rows ? String(connection.max_rows) : '',
          query_timeout_seconds: connection.query_timeout_seconds
            ? String(connection.query_timeout_seconds)
            : '',
        });
      } else {
        const dialect = preselectedDialect || initialFormData.dialect;
        setFormData({
          ...initialFormData,
          dialect,
          port: String(DEFAULT_PORTS[dialect]),
        });
      }
      setErrors({});
      setSaveError(null);
      setTestResult(null);
      setShowDeleteConfirm(false);
    }
  }, [open, connection, preselectedDialect]);

  // Update port when dialect changes (only in add mode)
  const handleDialectChange = useCallback((value: string) => {
    const dialect = value as DatabaseDialect;
    setFormData((prev) => ({
      ...prev,
      dialect,
      port: isEditMode ? prev.port : String(DEFAULT_PORTS[dialect]),
    }));
  }, [isEditMode]);

  const handleInputChange = useCallback((field: keyof FormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
    // Clear save error when form changes
    if (saveError) {
      setSaveError(null);
    }
    // Clear test result when form changes
    if (testResult) {
      setTestResult(null);
    }
  }, [errors, saveError, testResult]);

  const validate = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Name must be 100 characters or less';
    }

    if (!formData.host.trim()) {
      newErrors.host = 'Host is required';
    }

    const port = parseInt(formData.port, 10);
    if (isNaN(port) || port < 1 || port > 65535) {
      newErrors.port = 'Port must be between 1 and 65535';
    }

    if (!formData.database_name.trim()) {
      newErrors.database_name = 'Database name is required';
    }

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    }

    // Password required only for new connections
    if (!isEditMode && !formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, isEditMode]);

  const handleTest = useCallback(async () => {
    if (!validate()) return;

    setIsTesting(true);
    setTestResult(null);
    setSaveError(null);

    try {
      const testData: TestConnectionRequest = {
        dialect: formData.dialect,
        host: formData.host.trim(),
        port: parseInt(formData.port, 10),
        database_name: formData.database_name.trim(),
        username: formData.username.trim(),
        password: formData.password,
        ssl_mode: formData.ssl_mode || undefined,
      };

      const result = await onTest(testData);
      setTestResult(result);
    } catch (err) {
      setTestResult({
        success: false,
        error: err instanceof Error ? err.message : 'Connection test failed',
      });
    } finally {
      setIsTesting(false);
    }
  }, [formData, onTest, validate]);

  const handleSave = useCallback(async () => {
    if (!validate()) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      if (isEditMode) {
        const updateData: UpdateConnectionRequest = {
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          dialect: formData.dialect,
          host: formData.host.trim(),
          port: parseInt(formData.port, 10),
          database_name: formData.database_name.trim(),
          username: formData.username.trim(),
          password: formData.password || undefined, // Only send if changed
          ssl_mode: formData.ssl_mode || undefined,
          read_only: formData.read_only,
          max_rows: formData.max_rows ? parseInt(formData.max_rows, 10) : undefined,
          query_timeout_seconds: formData.query_timeout_seconds
            ? parseInt(formData.query_timeout_seconds, 10)
            : undefined,
        };
        await onSave(updateData);
      } else {
        const createData: CreateConnectionRequest = {
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          dialect: formData.dialect,
          host: formData.host.trim(),
          port: parseInt(formData.port, 10),
          database_name: formData.database_name.trim(),
          username: formData.username.trim(),
          password: formData.password,
          ssl_mode: formData.ssl_mode || undefined,
          read_only: formData.read_only,
          max_rows: formData.max_rows ? parseInt(formData.max_rows, 10) : undefined,
          query_timeout_seconds: formData.query_timeout_seconds
            ? parseInt(formData.query_timeout_seconds, 10)
            : undefined,
        };
        await onSave(createData);
      }
      onClose();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save connection');
    } finally {
      setIsSaving(false);
    }
  }, [formData, isEditMode, onSave, onClose, validate]);

  const handleDelete = useCallback(async () => {
    if (!connection || !onDelete) return;

    setIsDeleting(true);
    setSaveError(null);

    try {
      await onDelete(connection.id);
      onClose();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to delete connection');
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  }, [connection, onDelete, onClose]);

  return (
    <Modal open={open} onClose={onClose} size="lg">
      <ModalHeader
        title={isEditMode ? 'Edit Connection' : 'Add Connection'}
        description={isEditMode ? 'Update database connection settings' : 'Configure a new database connection'}
        onClose={onClose}
      />

      <ModalBody>
        {saveError && (
          <Alert variant="error" className={styles.alert}>
            {saveError}
          </Alert>
        )}

        {testResult && (
          <Alert
            variant={testResult.success ? 'success' : 'error'}
            className={styles.alert}
          >
            {testResult.success
              ? `Connection successful${testResult.latency_ms ? ` (${testResult.latency_ms}ms)` : ''}`
              : testResult.error || 'Connection failed'}
          </Alert>
        )}

        <div className={styles.form}>
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Basic Information</h3>
            <div className={styles.row}>
              <Input
                label="Connection Name"
                placeholder="My Production Database"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                error={!!errors.name}
                helpText={errors.name}
                required
              />
            </div>
            <div className={styles.row}>
              <TextArea
                label="Description"
                placeholder="Optional description for this connection"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={2}
              />
            </div>
          </div>

          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Connection Details</h3>
            <div className={styles.row}>
              <Select
                label="Database Type"
                options={DIALECT_OPTIONS}
                value={formData.dialect}
                onChange={handleDialectChange}
              />
            </div>
            <div className={styles.rowTwoCol}>
              <Input
                label="Host"
                placeholder="localhost or db.example.com"
                value={formData.host}
                onChange={(e) => handleInputChange('host', e.target.value)}
                error={!!errors.host}
                helpText={errors.host}
                required
              />
              <Input
                label="Port"
                type="number"
                placeholder={String(DEFAULT_PORTS[formData.dialect])}
                value={formData.port}
                onChange={(e) => handleInputChange('port', e.target.value)}
                error={!!errors.port}
                helpText={errors.port}
                required
              />
            </div>
            <div className={styles.row}>
              <Input
                label="Database Name"
                placeholder="mydb"
                value={formData.database_name}
                onChange={(e) => handleInputChange('database_name', e.target.value)}
                error={!!errors.database_name}
                helpText={errors.database_name}
                required
              />
            </div>
          </div>

          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Authentication</h3>
            <div className={styles.rowTwoCol}>
              <Input
                label="Username"
                placeholder="db_user"
                value={formData.username}
                onChange={(e) => handleInputChange('username', e.target.value)}
                error={!!errors.username}
                helpText={errors.username}
                required
              />
              <Input
                label={isEditMode ? 'Password (leave blank to keep current)' : 'Password'}
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                error={!!errors.password}
                helpText={errors.password}
                required={!isEditMode}
              />
            </div>
            <div className={styles.row}>
              <Select
                label="SSL Mode"
                options={SSL_MODE_OPTIONS}
                value={formData.ssl_mode}
                onChange={(value) => handleInputChange('ssl_mode', value)}
              />
            </div>
          </div>

          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Options</h3>
            <div className={styles.row}>
              <Toggle
                label="Read Only"
                checked={formData.read_only}
                onChange={(e) => handleInputChange('read_only', e.target.checked)}
              />
            </div>
            <div className={styles.rowTwoCol}>
              <Input
                label="Max Rows"
                type="number"
                placeholder="1000"
                value={formData.max_rows}
                onChange={(e) => handleInputChange('max_rows', e.target.value)}
                helpText="Maximum rows returned per query (optional)"
              />
              <Input
                label="Query Timeout (seconds)"
                type="number"
                placeholder="30"
                value={formData.query_timeout_seconds}
                onChange={(e) => handleInputChange('query_timeout_seconds', e.target.value)}
                helpText="Query timeout in seconds (optional)"
              />
            </div>
          </div>
        </div>
      </ModalBody>

      <ModalFooter>
        <div className={styles.footer}>
          <div className={styles.footerLeft}>
            {isEditMode && onDelete && (
              <>
                {showDeleteConfirm ? (
                  <div className={styles.deleteConfirm}>
                    <span>Delete this connection?</span>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={handleDelete}
                      disabled={isDeleting}
                    >
                      {isDeleting ? <Spinner size="sm" /> : 'Yes, Delete'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={isDeleting}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="ghost"
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    <Trash2 size={16} />
                    Delete
                  </Button>
                )}
              </>
            )}
          </div>
          <div className={styles.footerRight}>
            <Button
              variant="secondary"
              onClick={handleTest}
              disabled={isTesting || isSaving}
            >
              {isTesting ? <Spinner size="sm" /> : <Zap size={16} />}
              Test Connection
            </Button>
            <Button variant="ghost" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={isSaving || isTesting}
            >
              {isSaving ? <Spinner size="sm" /> : null}
              {isEditMode ? 'Save Changes' : 'Add Connection'}
            </Button>
          </div>
        </div>
      </ModalFooter>
    </Modal>
  );
}
